import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import MobileMenu from './MobileMenu';
import { useScrollToSection } from '../hooks/useScrollToSection';

const Navbar = ({ links }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const scrollToSection = useScrollToSection();
  const location = useLocation();

  // Always use these links regardless of what is passed from props
  const navLinks = [
    { section: 'features', label: 'Features', key: 'nav-features' },
    { section: 'how-it-works', label: 'How it Works', key: 'nav-how-it-works' },
    { section: 'pricing', label: 'Pricing', key: 'nav-pricing' },
  ];

  return (
    <nav className="fixed w-full top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img src="/logo.svg" alt="Link Spaghetti Logo" className="h-8 mr-2" />
            <span className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent group-hover:from-purple-700 group-hover:to-pink-700 transition-all duration-200">
              Link Spaghetti
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link, index) => (
              <button
                key={link.key}
                onClick={() => scrollToSection(link.section)}
                className="text-gray-600 hover:text-gray-900 font-medium transition-colors duration-200 animate-fade-in-down bg-transparent border-none cursor-pointer"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Desktop CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <Link
              to="/signin"
              className="text-gray-700 hover:text-gray-900 px-4 py-2 transition-colors duration-200 animate-fade-in"
              style={{ animationDelay: '300ms' }}
            >
              Sign In
            </Link>
            <Link
              to="/signup"
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-200 transform hover:scale-105 hover:shadow-lg animate-fade-in"
              style={{ animationDelay: '400ms' }}
            >
              Get Started
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="md:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            aria-label="Open mobile menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)}>
        <div className="space-y-6">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center group"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <img src="/logo.svg" alt="Link Spaghetti Logo" className="h-8 mr-2" />
            <span className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Link Spaghetti
            </span>
          </Link>

          {/* Mobile Navigation Links */}
          <nav className="space-y-4">
            {navLinks.map((link) => (
              <button
                key={link.key}
                onClick={() => {
                  scrollToSection(link.section);
                  setIsMobileMenuOpen(false);
                }}
                className="block text-lg text-gray-700 hover:text-purple-600 transition-colors bg-transparent border-none cursor-pointer w-full text-left"
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Mobile CTA Buttons */}
          <div className="space-y-3 pt-6 border-t">
            <Link
              to="/signin"
              className="block w-full text-center text-gray-700 hover:text-gray-900 px-4 py-3 rounded-lg border border-gray-200 hover:border-gray-300 transition-all"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Sign In
            </Link>
            <Link
              to="/signup"
              className="block w-full text-center bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-3 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Get Started Free
            </Link>
          </div>
        </div>
      </MobileMenu>
    </nav>
  );
};

export default Navbar; 